import type { TFunction, TOptions } from 'i18next';

/**
 * Value passed to the translation layer when a string has sprintf-style
 * placeholders like `"Hello %s, copy sent to %s"`.
 * First element is the i18n key; the rest are substituted for each `%s` in order.
 */
export type I18nMessageWithParams = [key: string, ...args: SprintfArg[]];

export type SprintfArg = string | number | boolean;

/**
 * Replaces `%%` with a literal `%`, then each `%s` (in order) with the
 * corresponding argument. Unmatched `%s` stay as-is; extra arguments are ignored.
 */
export function applySprintfStyle(template: string, args: readonly SprintfArg[]): string {
  const percentPlaceholder = '\uE000';
  let s = template.split('%%').join(percentPlaceholder);
  for (const arg of args) {
    if (!s.includes('%s')) {
      break;
    }
    s = s.replace('%s', String(arg));
  }
  return s.split(percentPlaceholder).join('%');
}

type TranslateI18nInput = string | I18nMessageWithParams;

/**
 * Resolves a key with `t`, then if a tuple was passed, substitutes `%s` / `%%`
 * in the translated string.
 * The app `i18n` instance already wraps `t` so you can also call
 * `useTranslation().t(['key', ...args])` with no other imports.
 *
 * @example
 * t('common.ok');
 * t(['errors.emailInUse', 'test@test.se']);
 * t(['errors.twoSlots', 'a@b.c', 2]);
 */
export function translateI18nMessage(
  t: TFunction,
  keyOrTuple: TranslateI18nInput,
  options?: TOptions,
): string {
  if (typeof keyOrTuple === 'string') {
    return t(keyOrTuple, { ...options }) as string;
  }
  const [key, ...args] = keyOrTuple;
  const template = t(key, { ...options }) as string;
  const withPlaceholders = applySprintfStyle(template, args);
  if (args.length > 0 && !template.includes('%s')) {
    return [withPlaceholders, ...args.map((a) => String(a))].join(' — ');
  }
  return withPlaceholders;
}

export function isI18nMessageWithParams(
  v: unknown,
): v is I18nMessageWithParams {
  return (
    Array.isArray(v) &&
    v.length >= 1 &&
    typeof v[0] === 'string' &&
    v.every(
      (item, i) => i === 0 || ['string', 'number', 'boolean'].includes(typeof item),
    )
  );
}

export type ApiErrorPayload = {
  errorCode?: string;
  errorMessage?: string;
  errorDetails?: unknown;
};

const isVisiblyEmpty = (s: string | undefined) => s == null || s.trim() === '';

/** Shown when i18n has no string for a key so the notification is still traceable. */
const formatI18nTupleForTrace = (tuple: I18nMessageWithParams) =>
  tuple.map((part) => String(part)).join(' | ');

/**
 * Prefer i18n + sprintf from `error_details` (e.g. `["profile.someKey", "a@b.c"]`);
 * then `error_code` as a key, then raw `error_message`, then a fallback i18n key.
 *
 * If a key in `error_details` is not in the loaded bundles, `t` may be empty. Then we
 * show the key and arguments (e.g. `profile.errorDuplicateDetails | im@…`) for easier
 * tracing, instead of a blank string.
 */
const lastResortMessage = (payload: ApiErrorPayload): string => {
  const { errorCode, errorMessage, errorDetails } = payload;
  const bits: string[] = [];
  if (errorCode) {
    bits.push(errorCode);
  }
  if (errorMessage) {
    bits.push(errorMessage);
  }
  if (errorDetails != null) {
    try {
      bits.push(typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails));
    } catch {
      bits.push(String(errorDetails));
    }
  }
  const joined = bits.filter((b) => !isVisiblyEmpty(b)).join(' — ');
  return joined || 'Error';
};

export function translateFromApiErrorPayload(
  t: TFunction,
  payload: ApiErrorPayload,
  fallbackKey: string = 'common.error',
): string {
  const { errorCode, errorMessage, errorDetails } = payload;
  if (isI18nMessageWithParams(errorDetails)) {
    const fromTuple = translateI18nMessage(t, errorDetails);
    if (!isVisiblyEmpty(fromTuple)) {
      return fromTuple;
    }
    const trace = formatI18nTupleForTrace(errorDetails);
    if (!isVisiblyEmpty(trace)) {
      return trace;
    }
  }
  if (errorCode) {
    const fromCode = t(errorCode) as string;
    const looksUntranslated = isVisiblyEmpty(fromCode) || fromCode === errorCode;
    if (!looksUntranslated) {
      return fromCode;
    }
    if (errorMessage && !isVisiblyEmpty(errorMessage)) {
      return errorMessage;
    }
    if (!isVisiblyEmpty(fromCode)) {
      return fromCode;
    }
    return errorCode;
  }
  if (errorMessage && !isVisiblyEmpty(errorMessage)) {
    return errorMessage;
  }
  const fromFallback = t(fallbackKey) as string;
  if (!isVisiblyEmpty(fromFallback)) {
    return fromFallback;
  }
  return lastResortMessage(payload);
}
